/**
 * Onboarding Store
 *
 * Zustand store for tracking onboarding state.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { onboardingAPI, usageAPI, alishaAPI } from '../services/api';

export type OnboardingPath = 'path_a' | 'path_b' | null;
export type BusinessStage = 'new_idea' | 'need_idea' | 'working_on' | null;

export interface PathAOutputs {
  oneSentencePitch: string;
  problemStatement: string;
  targetCustomer: string;
  marketValidation: string;
  companyName: string;
  goNoGo: 'go' | 'no_go' | null;
  goNoGoReasoning: string[];
  nextSteps: string[];
  whatFoundryWillDo: string[];
}

export interface PathBFormData {
  businessStatus: string;
  companyName: string;
  description: string;
  industry: string;
  teamSize: string;
  revenue: string;
  biggestChallenge: string;
  timeSink: string;
  otherChallenge?: string;
  otherTimeSink?: string;
}

export interface PathBOutputs {
  assessmentReport: {
    currentState: string;
    gaps: string[];
    opportunities: string[];
    timeAudit: string;
  };
  quickWin: {
    category: string;
    title: string;
    content: string;
  };
  recommendation: {
    stage: number;
    operators: string[];
    estimatedImpact: string[];
    monthlyInvestment: number;
  };
}

interface OnboardingState {
  // Current state
  step: 'welcome' | 'path_selection' | 'chat' | 'qualifier' | 'review' | 'summary' | 'complete';
  path: OnboardingPath;
  businessStage: BusinessStage;

  // Path A data
  pathAOutputs: PathAOutputs | null;

  // Path B data
  pathBFormData: PathBFormData | null;
  pathBOutputs: PathBOutputs | null;

  // Message tracking
  messagesUsed: number;
  messageLimit: number;

  // Ready state for outputs
  outputsReadyForReview: boolean;

  // Completion
  completedAt: Date | null;

  // Post-onboarding
  welcomeShown: boolean;

  // Conversation state
  hasConversation: boolean;

  // Actions
  setStep: (step: OnboardingState['step']) => void;
  setPath: (path: OnboardingPath) => void;
  setBusinessStage: (stage: BusinessStage) => void;
  setPathAOutputs: (outputs: PathAOutputs) => void;
  setPathBFormData: (data: PathBFormData) => void;
  setPathBOutputs: (outputs: PathBOutputs) => void;
  incrementMessages: () => void;
  setOutputsReadyForReview: (ready: boolean) => void;
  completeOnboarding: () => Promise<void>;
  hydrateFromBackend: () => Promise<void>;
  dismissWelcome: () => void;
  reset: () => void;
}

const initialState = {
  step: 'welcome' as const,
  path: null,
  businessStage: null,
  pathAOutputs: null,
  pathBFormData: null,
  pathBOutputs: null,
  messagesUsed: 0,
  messageLimit: 50,
  outputsReadyForReview: false,
  completedAt: null,
  welcomeShown: false,
  hasConversation: false,
};

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set) => ({
      ...initialState,

      setStep: (step) => set({ step }),

      setPath: (path) => set({ path }),

      setBusinessStage: (businessStage) => set({ businessStage }),

      setPathAOutputs: (pathAOutputs) => set({ pathAOutputs }),

      setPathBFormData: (pathBFormData) => set({ pathBFormData }),

      setPathBOutputs: (pathBOutputs) => set({ pathBOutputs }),

      incrementMessages: () =>
        set((state) => ({
          messagesUsed: state.messagesUsed + 1,
        })),

      setOutputsReadyForReview: (ready) => set({ outputsReadyForReview: ready }),

      completeOnboarding: async () => {
        set({ step: 'complete', completedAt: new Date() });

        try {
          // Mark as complete in backend
          await onboardingAPI.complete();

          // Transfer onboarding conversation to Exchange/Alisha chat
          console.log('[Onboarding] Transferring conversation to Exchange...');
          try {
            const convData = await onboardingAPI.getConversation();

            if (convData.conversation?.conversationHistory && convData.conversation.conversationHistory.length > 0) {
              // Convert onboarding format to ChatMessage format
              const formatTime = (timestamp?: string) => {
                const date = timestamp ? new Date(timestamp) : new Date();
                return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
              };

              const transferredMessages: Array<{ id: string; sender: string; text: string; time: string }> =
                convData.conversation.conversationHistory.map((msg, index) => ({
                  id: `onboarding-${index}-${Date.now()}`,
                  sender: msg.role === 'user' ? 'user' : 'iris',
                  text: msg.content,
                  time: formatTime(convData.conversation!.lastUpdated),
                }));

              // Save to Alisha conversation endpoint
              await alishaAPI.saveConversation(transferredMessages);
              console.log(`[Onboarding] ✅ Transferred ${transferredMessages.length} messages to Exchange`);

              // Trigger Alisha chat store to reload from backend
              // This ensures the conversation appears in Exchange immediately
              const { useAlishaChatStore } = await import('./alishaChat');
              await useAlishaChatStore.getState().hydrateFromBackend();
              console.log('[Onboarding] ✅ Reloaded Alisha chat store with transferred messages');
            } else {
              console.log('[Onboarding] No conversation to transfer');
            }
          } catch (transferError) {
            console.error('[Onboarding] Failed to transfer conversation:', transferError);
            // Don't fail onboarding completion if transfer fails
          }
        } catch { /* best-effort */ }
      },

      hydrateFromBackend: async () => {
        try {
          const state = await onboardingAPI.getState();
          console.log('[Onboarding] Hydrated from backend:', state);

          // Check if user has existing conversation
          let hasConversation = false;
          if (state.path && !state.isComplete) {
            try {
              const convData = await onboardingAPI.getConversation();
              hasConversation = !!(convData.conversation && convData.conversation.conversationHistory && convData.conversation.conversationHistory.length > 0);
              console.log('[Onboarding] Has conversation:', hasConversation, 'messages:', convData.conversation?.conversationHistory?.length);
            } catch (convErr) {
              console.error('[Onboarding] Failed to check conversation:', convErr);
            }
          }

          // Load actual message usage from backend
          try {
            const usage = await usageAPI.checkAlishaQuota();
            console.log('[Onboarding] Loaded usage from backend:', usage);
            set({ messagesUsed: usage.current, messageLimit: usage.limit });
          } catch (usageErr) {
            console.error('[Onboarding] Failed to load usage from backend:', usageErr);
            // Fall back to default/localStorage values
          }

          if (state.isComplete) {
            set({ step: 'complete', completedAt: new Date(state.completedAt!), path: state.path as OnboardingPath, hasConversation });
          } else if (state.path) {
            console.log('[Onboarding] Setting path:', state.path, 'step:', state.step);

            // Restore outputs if they exist in backend
            const updates: Partial<OnboardingState> = {
              step: state.step as OnboardingState['step'],
              path: state.path as OnboardingPath,
              businessStage: state.businessStage as BusinessStage,
              hasConversation
            };

            // Restore Path A or Path B outputs from backend
            if (state.outputs && Object.keys(state.outputs).length > 0) {
              if (state.path === 'path_a') {
                updates.pathAOutputs = state.outputs as unknown as PathAOutputs;
                console.log('[Onboarding] Restored Path A outputs from backend');
              } else if (state.path === 'path_b') {
                updates.pathBOutputs = state.outputs as unknown as PathBOutputs;
                console.log('[Onboarding] Restored Path B outputs from backend');
              }
            }

            set(updates);
          } else {
            console.log('[Onboarding] No path in backend state, path is:', state.path);
            set({ hasConversation });
          }
        } catch (err) {
          console.error('[Onboarding] Failed to hydrate from backend:', err);
          // Backend unreachable — fall through to localStorage
        }
      },

      dismissWelcome: () => set({ welcomeShown: true }),

      reset: () => set(initialState),
    }),
    {
      name: 'aerware-onboarding',
    }
  )
);

/**
 * Check if user can send more messages
 */
export function canSendMessage(): boolean {
  const { messagesUsed, messageLimit } = useOnboardingStore.getState();
  return messagesUsed < messageLimit;
}

/**
 * Get remaining messages
 */
export function getRemainingMessages(): number {
  const { messagesUsed, messageLimit } = useOnboardingStore.getState();
  return messageLimit - messagesUsed;
}

/**
 * Check if onboarding is complete
 */
export function isOnboardingComplete(): boolean {
  return useOnboardingStore.getState().completedAt !== null;
}
